import * as React from "react";

import { FOCUS_RING_FRAME, FOCUS_RING_FRAME_PEER } from "../lib/focus";
import { cn } from "../lib/utils";
import { useFieldControl } from "./field";
import { Icon, type IconComponent } from "./icon";

/**
 * v2 Input (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * Heights match Button (sm 28 / md 32 / lg 36), radius 6, transparent fill
 * (break #39): the field takes the surface it sits on, the `--input` border
 * carries the boundary (≥3:1 on page, card and popover in both modes).
 * Typed text is Inter (break #40).
 *
 * Structure: wrapper > [leading icon] input [suffix] [action] frame. The frame
 * is an empty span AFTER the input that draws border, fill and focus ring, so
 * `peer-*` variants style it from the input's own state. A wrapper-level
 * `focus-within` would also light up when the action button has focus.
 *
 * States: hover darkens the border (break #41); focus paints a 2px outline
 * over the border, inset by 1px, so nothing moves (break #38); invalid is red;
 * read-only is a grey fill with a light border (break #42); disabled is the
 * editable look at 50%. `data-hover` / `data-focus` on the input show hover and
 * focus statically in stories, as on Button; apps never set them.
 */

export type ControlState = "default" | "invalid" | "readonly" | "disabled";

export function controlState({
  disabled,
  readOnly,
  invalid,
}: {
  disabled?: boolean;
  readOnly?: boolean;
  invalid?: React.AriaAttributes["aria-invalid"];
}): ControlState {
  if (disabled) return "disabled";
  if (readOnly) return "readonly";
  if (invalid === true || invalid === "true") return "invalid";
  return "default";
}

/** Frame classes per state, driven by the preceding `peer` control (the input; DateRangePicker's trigger). */
export const PEER_FRAME: Record<ControlState, string> = {
  default: cn("border-input group-hover/input:border-ink-muted peer-data-hover:border-ink-muted", FOCUS_RING_FRAME_PEER),
  invalid: cn("border-destructive", FOCUS_RING_FRAME_PEER, "outline-destructive"),
  readonly: cn("border-border bg-muted", FOCUS_RING_FRAME_PEER),
  disabled: "border-input",
};

/** The same states on an element that is its own frame (Textarea). */
export const SELF_FRAME: Record<ControlState, string> = {
  default: cn("border-input hover:border-ink-muted data-hover:border-ink-muted", FOCUS_RING_FRAME),
  invalid: cn("border-destructive", FOCUS_RING_FRAME, "outline-destructive"),
  readonly: cn("border-border bg-muted placeholder:text-ink-muted", FOCUS_RING_FRAME),
  disabled: "cursor-not-allowed border-input opacity-50",
};

export type InputSize = "sm" | "md" | "lg";

/** Height, padding and text per size; Select and Combobox triggers share it. */
export const INPUT_SIZE: Record<InputSize, string> = {
  sm: "h-7 px-2 text-caption",
  md: "h-8 px-3 text-body",
  lg: "h-9 px-4 text-body",
};

/** With an action the xs IconButton (24) sits as far from the edge as from top and bottom. */
export const ACTION_PADDING: Record<InputSize, string> = {
  sm: "pr-0.5",
  md: "pr-1",
  lg: "pr-1.5",
};

// `size` is the native character-width attribute; ours is the control height.
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  size?: InputSize;
  /** Decorative icon before the text, 16px, muted (e.g. Search). */
  leadingIcon?: IconComponent;
  /** Static text after the value, e.g. a unit ("€"). */
  suffix?: React.ReactNode;
  /** One interactive element at the end, normally `<IconButton size="xs">` (clear, copy, show password). */
  action?: React.ReactNode;
  /** Goes on the wrapper: width and margins. */
  className?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(rawProps, ref) {
  const { size = "md", leadingIcon, suffix, action, className, ...props } = useFieldControl(rawProps);
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const state = controlState({ disabled: props.disabled, readOnly: props.readOnly, invalid: props["aria-invalid"] });

  return (
    <div
      className={cn(
        "group/input relative isolate flex w-full min-w-0 items-center gap-1.5 rounded-md font-body",
        INPUT_SIZE[size],
        action != null && ACTION_PADDING[size],
        state === "disabled" ? "cursor-not-allowed opacity-50" : "cursor-text",
        className,
      )}
      // Clicks on the padding, icon or suffix focus the input; the action handles its own.
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget || state === "disabled") return;
        event.preventDefault();
        inputRef.current?.focus();
      }}
    >
      {leadingIcon && <Icon icon={leadingIcon} className="pointer-events-none text-ink-muted" />}
      <input
        ref={inputRef}
        className={cn(
          "peer h-full min-w-0 flex-1 bg-transparent text-foreground outline-hidden placeholder:text-muted-foreground",
          "disabled:cursor-not-allowed",
          state === "readonly" && "placeholder:text-ink-muted",
        )}
        {...props}
      />
      {suffix != null && <span className="pointer-events-none shrink-0 text-ink-muted">{suffix}</span>}
      {action != null && <span className="flex shrink-0 items-center">{action}</span>}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 -z-10 rounded-md border transition-colors",
          PEER_FRAME[state],
        )}
      />
    </div>
  );
});

export { Input };
